// src/features/Investing/stores/dealStore.js
import { create } from 'zustand';
import { authedApiFetch } from '../../../utils/Api';

const useDealStore = create((set, get) => ({
	// Core data
	deal: null,
	dealStory: null,
	objectAttributes: [],
	objectMetrics: [],
	objectFacts: [],
	objectInsights: [],
	prompts: [],
	sourceMonitors: [],

	// Sort AMFI items with is_key at the top
	sortByIsKey: (items) => {
		return [...items].sort((a, b) => {
			// Sort by is_key (true values first)
			if (a.is_key && !b.is_key) return -1;
			if (!a.is_key && b.is_key) return 1;
			return 0;
		});
	},

	// Loading state
	loading: {
		deal: false,
		dealStory: false,
		attributes: false,
		metrics: false,
		facts: false,
		insights: false,
		prompts: false,
		sources: false,
	},

	// Fetch just the deal data
	fetchDeal: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, deal: true } }));
		try {
			const dealData = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}`,
				method: 'GET',
			});
			set({
				deal: dealData,
				loading: { ...get().loading, deal: false },
			});
			return dealData;
		} catch (error) {
			console.error('Error fetching deal:', error);
			set((state) => ({ loading: { ...state.loading, deal: false } }));
			return null;
		}
	},

	// Fetch attributes for a deal
	fetchAttributes: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, attributes: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_attributes`,
				method: 'GET',
			});
			set({
				objectAttributes: data,
				loading: { ...get().loading, attributes: false },
			});
			return data;
		} catch (error) {
			console.error('Error fetching attributes:', error);
			set((state) => ({ loading: { ...state.loading, attributes: false } }));
			return null;
		}
	},

	// Add a new attribute
	addAttribute: async (tenantId, dealId, data) => {
		try {
			const newAttribute = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_attributes`,
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_attribute: data },
			});

			set((state) => ({
				objectAttributes: [...state.objectAttributes, newAttribute],
			}));

			return newAttribute;
		} catch (error) {
			console.error('Error adding attribute:', error);
			throw error;
		}
	},

	// Update an existing attribute
	updateAttribute: async (tenantId, dealId, attribute) => {
		try {
			const updatedAttribute = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_attributes/${attribute.id}`,
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_attribute: attribute },
			});

			set((state) => ({
				objectAttributes: state.objectAttributes.map((a) =>
					a.id === updatedAttribute.id ? updatedAttribute : a
				),
			}));

			return updatedAttribute;
		} catch (error) {
			console.error('Error updating attribute:', error);
			throw error;
		}
	},

	// Delete an attribute
	deleteAttribute: async (tenantId, dealId, attributeId) => {
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_attributes/${attributeId}`,
				method: 'DELETE',
			});

			set((state) => ({
				objectAttributes: state.objectAttributes.filter(
					(a) => a.id !== attributeId
				),
			}));

			return true;
		} catch (error) {
			console.error('Error deleting attribute:', error);
			throw error;
		}
	},

	// Fetch metrics for a deal
	fetchMetrics: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, metrics: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_metrics`,
				method: 'GET',
			});
			set({
				objectMetrics: data,
				loading: { ...get().loading, metrics: false },
			});
			return data;
		} catch (error) {
			console.error('Error fetching metrics:', error);
			set((state) => ({ loading: { ...state.loading, metrics: false } }));
			return null;
		}
	},

	// Add a new metric
	addMetric: async (tenantId, dealId, data) => {
		try {
			const newMetric = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_metrics`,
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_metric: data },
			});

			set((state) => ({
				objectMetrics: [...state.objectMetrics, newMetric],
			}));

			return newMetric;
		} catch (error) {
			console.error('Error adding metric:', error);
			throw error;
		}
	},

	// Update an existing metric
	updateMetric: async (tenantId, dealId, metric) => {
		try {
			const updatedMetric = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_metrics/${metric.id}`,
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_metric: metric },
			});

			set((state) => ({
				objectMetrics: state.objectMetrics.map((m) =>
					m.id === updatedMetric.id ? updatedMetric : m
				),
			}));

			return updatedMetric;
		} catch (error) {
			console.error('Error updating metric:', error);
			throw error;
		}
	},

	// Delete a metric
	deleteMetric: async (tenantId, dealId, metricId) => {
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_metrics/${metricId}`,
				method: 'DELETE',
			});

			set((state) => ({
				objectMetrics: state.objectMetrics.filter((m) => m.id !== metricId),
			}));

			return true;
		} catch (error) {
			console.error('Error deleting metric:', error);
			throw error;
		}
	},

	// Fetch facts for a deal
	fetchFacts: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, facts: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_facts`,
				method: 'GET',
			});
			set({
				objectFacts: data,
				loading: { ...get().loading, facts: false },
			});
			return data;
		} catch (error) {
			console.error('Error fetching facts:', error);
			set((state) => ({ loading: { ...state.loading, facts: false } }));
			return null;
		}
	},

	// Add a new fact
	addFact: async (tenantId, dealId, data) => {
		try {
			// Handle the content field (facts use content instead of value)
			const factData = { ...data };
			if (factData.value && !factData.content) {
				factData.content = factData.value;
				delete factData.value;
			}

			const newFact = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_facts`,
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_fact: factData },
			});

			set((state) => ({
				objectFacts: [...state.objectFacts, newFact],
			}));

			return newFact;
		} catch (error) {
			console.error('Error adding fact:', error);
			throw error;
		}
	},

	// Update an existing fact
	updateFact: async (tenantId, dealId, fact) => {
		try {
			// Handle the content field (facts use content instead of value)
			const factData = { ...fact };
			if (factData.value && !factData.content) {
				factData.content = factData.value;
				delete factData.value;
			}

			const updatedFact = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_facts/${fact.id}`,
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_fact: factData },
			});

			set((state) => ({
				objectFacts: state.objectFacts.map((f) =>
					f.id === updatedFact.id ? updatedFact : f
				),
			}));

			return updatedFact;
		} catch (error) {
			console.error('Error updating fact:', error);
			throw error;
		}
	},

	// Delete a fact
	deleteFact: async (tenantId, dealId, factId) => {
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_facts/${factId}`,
				method: 'DELETE',
			});

			set((state) => ({
				objectFacts: state.objectFacts.filter((f) => f.id !== factId),
			}));

			return true;
		} catch (error) {
			console.error('Error deleting fact:', error);
			throw error;
		}
	},

	// Fetch insights for a deal
	fetchInsights: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, insights: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_insights`,
				method: 'GET',
			});
			set({
				objectInsights: data,
				loading: { ...get().loading, insights: false },
			});
			return data;
		} catch (error) {
			console.error('Error fetching insights:', error);
			set((state) => ({ loading: { ...state.loading, insights: false } }));
			return null;
		}
	},

	// Add a new insight
	addInsight: async (tenantId, dealId, data) => {
		try {
			// Handle the content field (insights use content instead of value)
			const insightData = { ...data };
			if (insightData.value && !insightData.content) {
				insightData.content = insightData.value;
				delete insightData.value;
			}

			const newInsight = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_insights`,
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_insight: insightData },
			});

			set((state) => ({
				objectInsights: [...state.objectInsights, newInsight],
			}));

			return newInsight;
		} catch (error) {
			console.error('Error adding insight:', error);
			throw error;
		}
	},

	// Update an existing insight
	updateInsight: async (tenantId, dealId, insight) => {
		try {
			// Handle the content field (insights use content instead of value)
			const insightData = { ...insight };
			if (insightData.value && !insightData.content) {
				insightData.content = insightData.value;
				delete insightData.value;
			}

			const updatedInsight = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_insights/${insight.id}`,
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				payload: { object_insight: insightData },
			});

			set((state) => ({
				objectInsights: state.objectInsights.map((i) =>
					i.id === updatedInsight.id ? updatedInsight : i
				),
			}));

			return updatedInsight;
		} catch (error) {
			console.error('Error updating insight:', error);
			throw error;
		}
	},

	// Delete an insight
	deleteInsight: async (tenantId, dealId, insightId) => {
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/Deal/${dealId}/object_insights/${insightId}`,
				method: 'DELETE',
			});

			set((state) => ({
				objectInsights: state.objectInsights.filter((i) => i.id !== insightId),
			}));

			return true;
		} catch (error) {
			console.error('Error deleting insight:', error);
			throw error;
		}
	},

	// Fetch prompts for a tenant
	fetchPrompts: async (tenantId, options = {}) => {
		set((state) => ({ loading: { ...state.loading, prompts: true } }));
		try {
			// Build query params
			const queryParams = new URLSearchParams();

			// Default active to true unless explicitly set otherwise
			queryParams.append(
				'active',
				options.active !== undefined ? options.active : true
			);
			if (options.hierarchyLevel) {
				queryParams.append('hierarchy_level', options.hierarchyLevel);
			}
			if (options.promptType) {
				queryParams.append('prompt_type', options.promptType);
			}
			if (options.includeVersions) {
				queryParams.append('include_versions', options.includeVersions);
			}

			// Add include_tenant_default=true to get tenant default version content
			// The backend will now handle replacing content with tenant default content directly
			queryParams.append('include_tenant_default', true);

			const queryString = queryParams.toString();
			const endpoint = `/tenants/${tenantId}/prompts${queryString ? `?${queryString}` : ''}`;

			const data = await authedApiFetch({
				endpoint,
				method: 'GET',
			});

			// Sort prompts alphabetically by name
			const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
			set({
				prompts: sortedData,
				loading: { ...get().loading, prompts: false },
			});
			return sortedData;
		} catch (error) {
			console.error('Error fetching prompts:', error);
			set((state) => ({ loading: { ...state.loading, prompts: false } }));
			return null;
		}
	},

	// Fetch source monitors for a deal
	fetchSourceMonitors: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, sources: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}/source_monitors`,
				method: 'GET',
			});

			const processedData = data.map((source) => {
				const isDocument = source.item_config?.value === 'document';
				const fileName = source.name || source.file_name || '';
				const fileExtension = fileName.split('.').pop()?.toUpperCase();

				return {
					...source,
					type: source.item_config?.display_name,
					// Only add format for document types and if we could extract an extension
					...(isDocument && fileExtension && { format: fileExtension }),
				};
			});

			set({
				sourceMonitors: processedData,
				loading: { ...get().loading, sources: false },
			});

			return processedData;
		} catch (error) {
			console.error('Error fetching source monitors:', error);
			set((state) => ({ loading: { ...state.loading, sources: false } }));
			return null;
		}
	},



	// For deal story - fetch existing story
	fetchDealStory: async (tenantId, dealId) => {
		console.log('Fetching deal story:', tenantId, dealId);
		set((state) => ({ loading: { ...state.loading, dealStory: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}/deal_stories`,
				method: 'GET',
			});
			console.log('Deal story response:', data);
			if (data && data.length > 0) {
				set({
					dealStory: {
						content: data[0].content,
						generated_at: data[0].generated_at
					},
					loading: { ...get().loading, dealStory: false },
				});
				return data[0];
			} else {
				set({
					dealStory: null,
					loading: { ...get().loading, dealStory: false },
				});
				return null;
			}
		} catch (error) {
			console.error('Error fetching deal story:', error);
			set((state) => ({ loading: { ...state.loading, dealStory: false } }));
			return null;
		}
	},

	generateDealStory: async (tenantId, dealId) => {
		set((state) => ({ loading: { ...state.loading, dealStory: true } }));
		try {
			const data = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}/generate_story`,
				method: 'POST',
			});
			set({
				dealStory: data,
				loading: { ...get().loading, dealStory: false },
			});
			return data;
		} catch (error) {
			console.error('Error generating deal story:', error);
			set((state) => ({ loading: { ...state.loading, dealStory: false } }));
			return null;
		}
	},

	// Fetch all deal data at once
	fetchAllDealData: async (tenantId, dealId) => {
		await get().fetchDeal(tenantId, dealId);
		await get().fetchAttributes(tenantId, dealId);
		await get().fetchMetrics(tenantId, dealId);
		await get().fetchFacts(tenantId, dealId);
		await get().fetchInsights(tenantId, dealId);
		await get().fetchPrompts(tenantId);
		await get().fetchSourceMonitors(tenantId, dealId);
		// Don't fetch deal story automatically - only on demand
	},

	// Update core deal properties
	updateDeal: async (tenantId, dealId, dealData) => {
		try {
			const updatedDeal = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${dealId}`,
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				payload: { deal: dealData },
			});

			// Update the store with the new deal data
			set({ deal: updatedDeal });

			return updatedDeal;
		} catch (error) {
			console.error('Error updating deal:', error);
			throw error;
		}
	},
}));

export default useDealStore;
